import { useCallback } from 'react'
import { useGameStore } from '../store/gameStore'
import {
  postAction,
  sendDonation,
  acceptDonation,
  rejectDonation,
  resolveWoollyFurChoice,
  sendWinterTrade,
  sendColonyCollapseOffer,
  resolveColonyCollapse,
} from '../api/game'
import type { GameActionRequest } from '../types/api'
import { ApiError } from '../api/client'
import { formatPlayerFacingError } from '../utils/errorMessage'

export function useGameActions() {
  const { roomId, memberId, member, room, setError, setLoading } = useGameStore()
  const selfColor = member?.color ?? room?.members.find((knownMember) => knownMember.id === memberId)?.color ?? null

  const dispatchAction = useCallback(
    async (action: GameActionRequest) => {
      if (!roomId) return
      setLoading(true)
      setError(null)
      try {
        const result = await postAction(roomId, action)
        // Handle follow-ups
        if (result.follow_ups) {
          const store = useGameStore.getState()
          for (const followUp of result.follow_ups) {
            if (followUp.type === 'season_overview') {
              store.setShowSeasonOverview(true)
            }
          }
        }
        return result
      } catch (err) {
        if (err instanceof ApiError) {
          setError(formatPlayerFacingError(err.message, selfColor))
        } else {
          setError('An unexpected error occurred')
        }
        return null
      } finally {
        setLoading(false)
      }
    },
    [roomId, selfColor, setError, setLoading]
  )

  const forage = useCallback(
    (flowerId: number) => {
      if (!memberId) return
      return dispatchAction({ action: 'forage', player_id: memberId, flower_id: flowerId })
    },
    [memberId, dispatchAction]
  )

  const pollinate = useCallback(
    (flowerId: number) => {
      if (!memberId) return
      return dispatchAction({ action: 'pollinate', player_id: memberId, flower_id: flowerId })
    },
    [memberId, dispatchAction]
  )

  const buildBroodChamber = useCallback(
    (cellId: string) => {
      if (!memberId) return
      return dispatchAction({ action: 'build_brood_chamber', player_id: memberId, cell_id: cellId })
    },
    [memberId, dispatchAction]
  )

  const makeHoney = useCallback(
    (cellId: string) => {
      if (!memberId) return
      return dispatchAction({ action: 'make_honey', player_id: memberId, cell_id: cellId })
    },
    [memberId, dispatchAction]
  )

  const layEgg = useCallback(
    (cellId: string) => {
      if (!memberId) return
      return dispatchAction({ action: 'lay_egg', player_id: memberId, cell_id: cellId })
    },
    [memberId, dispatchAction]
  )

  const clearInfection = useCallback(
    (cellId: string) => {
      if (!memberId) return
      return dispatchAction({ action: 'clear_infection', player_id: memberId, cell_id: cellId })
    },
    [memberId, dispatchAction]
  )

  const evolve = useCallback(
    (evolutionId: number) => {
      if (!memberId) return
      return dispatchAction({ action: 'evolve', player_id: memberId, evolution_id: evolutionId })
    },
    [memberId, dispatchAction]
  )

  const pass = useCallback(() => {
    if (!memberId) return
    return dispatchAction({ action: 'pass', player_id: memberId })
  }, [memberId, dispatchAction])

  const tradeWithQueen = useCallback(
    (tradeId: string) => {
      if (!memberId) return
      return dispatchAction({ action: 'trade_with_queen', player_id: memberId, trade_id: tradeId })
    },
    [memberId, dispatchAction]
  )

  const finishQueenTrading = useCallback(() => {
    if (!memberId) return
    return dispatchAction({ action: 'finish_queen_trading', player_id: memberId })
  }, [memberId, dispatchAction])

  const donate = useCallback(
    async (receiverId: string, donations: Array<{ resource: string; amount: number }>) => {
      if (!roomId || !memberId) return
      setLoading(true)
      setError(null)
      try {
        await sendDonation(roomId, {
          donor_id: memberId,
          receiver_id: receiverId,
          donations,
        })
      } catch (err) {
        if (err instanceof ApiError) setError(formatPlayerFacingError(err.message, selfColor))
        else setError('Donation failed')
      } finally {
        setLoading(false)
      }
    },
    [roomId, memberId, selfColor, setError, setLoading]
  )

  const acceptDonationAction = useCallback(async () => {
    if (!roomId || !memberId) return
    setLoading(true)
    setError(null)
    try {
      await acceptDonation(roomId, { receiver_id: memberId })
    } catch (err) {
      if (err instanceof ApiError) setError(formatPlayerFacingError(err.message, selfColor))
      else setError('Failed to accept donation')
    } finally {
      setLoading(false)
    }
  }, [roomId, memberId, selfColor, setError, setLoading])

  const rejectDonationAction = useCallback(async () => {
    if (!roomId || !memberId) return
    setLoading(true)
    setError(null)
    try {
      await rejectDonation(roomId, { receiver_id: memberId })
    } catch (err) {
      if (err instanceof ApiError) setError(formatPlayerFacingError(err.message, selfColor))
      else setError('Failed to reject donation')
    } finally {
      setLoading(false)
    }
  }, [roomId, memberId, selfColor, setError, setLoading])

  const resolveWoollyFurAction = useCallback(
    async (resource: 'nectar' | 'pollen') => {
      if (!roomId || !memberId) return
      setLoading(true)
      setError(null)
      try {
        await resolveWoollyFurChoice(roomId, { player_id: memberId, resource })
      } catch (err) {
        if (err instanceof ApiError) setError(formatPlayerFacingError(err.message, selfColor))
        else setError('Failed to resolve Woolly Fur choice')
      } finally {
        setLoading(false)
      }
    },
    [roomId, memberId, selfColor, setError, setLoading]
  )

  const winterTradeAction = useCallback(
    async (nPropolis: number) => {
      if (!roomId || !memberId) return
      setLoading(true)
      setError(null)
      try {
        await sendWinterTrade(roomId, { player_id: memberId, n_propolis: nPropolis })
      } catch (err) {
        if (err instanceof ApiError) setError(formatPlayerFacingError(err.message, selfColor))
        else setError('Failed to trade at the winter market')
      } finally {
        setLoading(false)
      }
    },
    [roomId, memberId, selfColor, setError, setLoading]
  )

  const submitColonyCollapseOffer = useCallback(
    async (offers: Array<{ resource: string; amount: number }>) => {
      if (!roomId || !memberId) return
      setLoading(true)
      setError(null)
      try {
        await sendColonyCollapseOffer(roomId, { player_id: memberId, offers })
      } catch (err) {
        if (err instanceof ApiError) setError(formatPlayerFacingError(err.message, selfColor))
        else setError('Failed to submit offer')
      } finally {
        setLoading(false)
      }
    },
    [roomId, memberId, selfColor, setError, setLoading]
  )

  const resolveColonyCollapseAction = useCallback(async (force = false) => {
    if (!roomId || !memberId) return
    setLoading(true)
    setError(null)
    try {
      const result = await resolveColonyCollapse(roomId, { member_id: memberId, force }) as { follow_ups?: Array<{ type: string }> }
      if (result?.follow_ups) {
        const store = useGameStore.getState()
        for (const followUp of result.follow_ups) {
          if (followUp.type === 'colony_collapse_overview') {
            store.setShowColonyCollapseOverview(true)
          }
        }
      }
    } catch (err) {
      if (err instanceof ApiError) setError(formatPlayerFacingError(err.message, selfColor))
      else setError('Failed to resolve colony collapse')
    } finally {
      setLoading(false)
    }
  }, [roomId, memberId, selfColor, setError, setLoading])

  return {
    dispatchAction,
    forage,
    pollinate,
    buildBroodChamber,
    makeHoney,
    layEgg,
    clearInfection,
    evolve,
    pass,
    tradeWithQueen,
    finishQueenTrading,
    donate,
    acceptDonation: acceptDonationAction,
    rejectDonation: rejectDonationAction,
    resolveWoollyFur: resolveWoollyFurAction,
    winterTrade: winterTradeAction,
    submitColonyCollapseOffer,
    resolveColonyCollapse: resolveColonyCollapseAction,
  }
}
